import { Flex, Group } from '@mantine/core';
import { useEffect } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import CashflowContent from 'src/cashflows/CashflowContent';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useContentMeta } from 'src/UI/app-container';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { ChatMode } from 'src/UI/app-container/chat/types';
import FileUploadPage, {
  FileUploadTabNames,
} from 'src/data-capture/file-management/FileUploadPage';
import { FinancialContent } from 'src/financial/financial-data/FinancialContent';
import NonFinancialDataContent from 'src/non-financial-data/NonFinancialDataContent';
import { BackToClientsButton } from 'src/non-financial-data/v2/BackToClientsButton';
import { FamilyIndividualToggle } from 'src/non-financial-data/v2/FamilyIndividualToggle';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import { familyTabForIndividual } from 'src/non-financial-data/v2/family/tabMapping';
import { NonFinancialDataTabs } from 'src/non-financial-data/types';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import ClientMeetingsContent from 'src/meetings/client/ClientMeetingsContent';
import { NameFormat, formatName } from 'src/utils/nameFormatting';

const ClientTabs = {
  NonFinancialData: 'non-financial-data',
  FinancialData: 'financial-data',
  Cashflows: 'cashflows',
  Documents: 'document',
  Meetings: 'meetings',
} as const;
type TabName = (typeof ClientTabs)[keyof typeof ClientTabs];

export const CLIENT_TABS_REGEX = new RegExp(
  `/client/[^/]+/(${Object.values(ClientTabs).join('|')}).*`,
);

export default function ClientData() {
  const { clientId, client } = useCurrentClient();
  const { tab, sectionSlug } = useParams<{ tab: TabName; sectionSlug?: string }>();
  const { setContentPaneConfig } = useAppContainerContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  useContentMeta({ pageType: 'client', entityId: clientId, chatMode: ChatMode.CLIENT });

  const fullName = formatName(client?.firstName, client?.lastName, NameFormat.NATURAL);
  const familyName = client?.family?.name;
  const individualLabel = fullName || 'Individual';
  const familyLabel = familyName || 'Family';

  useEffect(() => {
    setContentPaneConfig({ p: 'xxl', bg: 'white' });
    return () => setContentPaneConfig({});
  }, [setContentPaneConfig]);

  const tabContent = getClientTabContent(tab);

  const hideChrome = tab === ClientTabs.FinancialData && sectionSlug === 'create';

  const toggleValue = searchParams.get('view') === 'family' ? 'family' : 'individual';

  const isFamilyEmptyState =
    toggleValue === 'family' && !client?.familyId && tab === ClientTabs.NonFinancialData;

  const { members, hasOtherMembers } = useFamilyMembers({
    enabled: !!client?.familyId,
  });
  const showMemberDropdown = hasOtherMembers;

  function handleToggleChange(value: 'family' | 'individual') {
    if (value === 'family') {
      if (client?.familyId) {
        navigate(`/family/${client.familyId}/${familyTabForIndividual(tab)}?member=${clientId}`);
      } else {
        navigate(
          `/client/${clientId}/${ClientTabs.NonFinancialData}/${NonFinancialDataTabs.PersonalDetails}?view=family`,
        );
      }
    } else {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        return next;
      });
    }
  }

  function handleMemberSelect(memberId: string) {
    navigate(`/client/${memberId}/${tab ?? ClientTabs.NonFinancialData}`);
  }

  const toggle = (
    <FamilyIndividualToggle
      value={toggleValue}
      onChange={handleToggleChange}
      familyLabel={familyLabel}
      individualLabel={individualLabel}
      members={showMemberDropdown ? members : undefined}
      onMemberSelect={showMemberDropdown ? handleMemberSelect : undefined}
      showGenericInactiveLabel
    />
  );

  const isKnownTab = tab != null && (Object.values(ClientTabs) as string[]).includes(tab);
  if (tab != null && !isKnownTab) {
    return <Navigate to={`/client/${clientId}/${ClientTabs.NonFinancialData}`} replace />;
  }

  return (
    <Flex direction="column" gap="lg" pos={'relative'} h={isFamilyEmptyState ? '100%' : undefined}>
      {!hideChrome && (
        <UnderlineTabs
          mode="router"
          routerTabsV2
          leadingContent={
            <Group gap="sm" wrap="nowrap">
              <BackToClientsButton />
              {toggle}
            </Group>
          }
          integrateBreadcrumb
          tabs={[
            {
              label: 'Profile',
              href: `/client/${clientId}/${ClientTabs.NonFinancialData}`,
            },
            {
              label: 'Financial',
              href: `/client/${clientId}/${ClientTabs.FinancialData}`,
            },
            {
              label: 'Cashflows',
              href: `/client/${clientId}/${ClientTabs.Cashflows}`,
            },
            {
              label: 'Documents',
              href: `/client/${clientId}/${ClientTabs.Documents}/${FileUploadTabNames.ToReview}`,
              matchHref: `/client/${clientId}/${ClientTabs.Documents}`,
            },
            {
              label: 'Meetings',
              href: `/client/${clientId}/${ClientTabs.Meetings}`,
            },
          ]}
        />
      )}

      {tabContent}
    </Flex>
  );
}

function getClientTabContent(tab: TabName | undefined) {
  if (tab === ClientTabs.NonFinancialData) {
    return <NonFinancialDataContent />;
  }
  if (tab === ClientTabs.FinancialData) {
    return <FinancialContent />;
  }
  if (tab === ClientTabs.Cashflows) {
    return <CashflowContent />;
  }
  if (tab === ClientTabs.Documents) {
    return <FileUploadPage />;
  }
  if (tab === ClientTabs.Meetings) {
    return <ClientMeetingsContent />;
  }
  return null;
}
